import type { ComponentPropsWithoutRef, ElementType, ReactNode } from 'react';
import { createElement } from 'react';
import { cx } from '../utils/cx.js';
import { Loader } from './Loader.js';
import styles from './Button.module.css';

export type ButtonVariant = 'primary' | 'secondaryPill' | 'tertiaryPill' | 'ghost' | 'danger' | 'soft';

/** Default: native `<button>`. Pass `as={Link}` (etc.) — `to`/`href` come from host props. */
export type ButtonProps<T extends ElementType = 'button'> = {
  as?: T;
  variant?: ButtonVariant;
  /** Compact sizes for tables and toolbars */
  size?: 'xs' | 'sm' | 'md';
  children: ReactNode;
  /** Shows spinner, disables control, sets aria-busy. */
  loading?: boolean;
  /** Only used when `as` is omitted (native `<button>`). */
  type?: HTMLButtonElement['type'];
} & Omit<ComponentPropsWithoutRef<T>, 'as' | 'children' | 'type'>;

export function Button<T extends ElementType = 'button'>(props: ButtonProps<T>) {
  const {
    as,
    variant = 'primary',
    size = 'md',
    className,
    type = 'button',
    children,
    loading = false,
    disabled,
    ...rest
  } = props;
  const Comp = (as ?? 'button') as ElementType;
  const isNativeButton = !as;
  return createElement(
    Comp,
    {
      ...(isNativeButton ? { type: type ?? 'button' } : {}),
      className: cx(styles.btn, styles[variant], styles[`sz_${size}`], styles.focus, loading && styles.loading, className),
      disabled: disabled || loading,
      'aria-busy': loading || undefined,
      ...rest,
    } as Record<string, unknown>,
    loading ? (
      <span className={styles.spinner} aria-hidden>
        <Loader
          size="sm"
          tone={variant === 'primary' || variant === 'danger' ? 'onBrand' : 'default'}
        />
      </span>
    ) : null,
    children,
  );
}
